import type { LoanIdentityDecision } from 'tio-data/requests/loan-identity-answer';
import type { HeldLoan, LoanVerification } from 'tio-data/requests/loan-verification';
import type { SlrAlert } from 'tio-data/requests/person';
import { toResolutionAlert, type ResolutionAlert } from 'tio-employee/utils/slr/resolution-alert';

/** Rollout flag for the member loan verification flow: the page, its alert and the upload hand-off. */
export const SLR_LOAN_VERIFICATION_FLAG = 'slrLoanVerification';

/** The member's pick for one held loan before submit: add it as new, or let it replace one loan. */
export type LoanChoice = { kind: 'new' } | { kind: 'replace'; existingLoanId: string };

/** Held loans still waiting on an answer; applied ones stay on the page but are never re-sent. */
export function pendingHeldLoans(verification: LoanVerification) {
  return verification.heldLoans.filter((loan) => loan.state === 'held');
}

/** Held loans whose answer is recorded but not yet applied: the upload's next reflection writes them. */
export function recordedHeldLoans(verification: LoanVerification) {
  return verification.heldLoans.filter((loan) => loan.state === 'answered');
}

/**
 * Whether the feed still holds this upload's loans on another account: the upload applies only once
 * every account's are answered. An alert answered this session no longer waits; a dismissed one does.
 */
export function anotherAccountWaits(
  feed: SlrAlert[],
  target: { documentId: string; accountId: string | null },
  isAnswered: (question: ResolutionAlert) => boolean
) {
  if (!target.accountId) return false;
  return feed.some((alert) => {
    const question = toResolutionAlert(alert);
    const accountId = alert.metadata['account-id'];
    return (
      question?.resolutionType === 'loan-identity' &&
      question.documentId === target.documentId &&
      typeof accountId === 'string' &&
      accountId !== '' &&
      accountId !== target.accountId &&
      !isAnswered(question)
    );
  });
}

/**
 * The existing loans strict 1:1 keeps away from `holdId`: those the server already marks in use,
 * and those another held loan on this page has picked.
 */
export function unavailableLoanIds(
  verification: LoanVerification,
  choices: Record<string, LoanChoice>,
  holdId: string
) {
  const taken = new Set(
    verification.existingLoans.filter((loan) => loan.inUse).map((loan) => loan.loanId)
  );
  for (const [otherHoldId, choice] of Object.entries(choices)) {
    if (otherHoldId !== holdId && choice.kind === 'replace') taken.add(choice.existingLoanId);
  }
  return taken;
}

/** The server's preview for one pairing, in cents; null when it sent none. */
export function becomesBalance(heldLoan: HeldLoan, existingLoanId: string) {
  return (
    heldLoan.previews.find((preview) => preview.existingLoanId === existingLoanId)
      ?.becomesBalance ?? null
  );
}

/** One decision per pending held loan, in page order; null until every one of them has a choice. */
export function decisionsFor(
  verification: LoanVerification,
  choices: Record<string, LoanChoice>
): LoanIdentityDecision[] | null {
  const decisions: LoanIdentityDecision[] = [];
  for (const loan of pendingHeldLoans(verification)) {
    const choice = choices[loan.holdId];
    if (!choice) return null;
    decisions.push({
      holdId: loan.holdId,
      existingLoanId: choice.kind === 'replace' ? choice.existingLoanId : null,
    });
  }
  return decisions.length ? decisions : null;
}

/** Drops the choices a re-read made impossible: a held loan no longer pending, or a loan now taken or gone. */
export function keepValidChoices(
  verification: LoanVerification,
  choices: Record<string, LoanChoice>
) {
  const kept: Record<string, LoanChoice> = {};
  const pendingIds = new Set(pendingHeldLoans(verification).map((loan) => loan.holdId));
  const existingIds = new Set(verification.existingLoans.map((loan) => loan.loanId));

  for (const [holdId, choice] of Object.entries(choices)) {
    if (!pendingIds.has(holdId)) continue;
    if (choice.kind === 'replace') {
      const stillThere = existingIds.has(choice.existingLoanId);
      if (
        !stillThere ||
        unavailableLoanIds(verification, kept, holdId).has(choice.existingLoanId)
      ) {
        continue;
      }
    }
    kept[holdId] = choice;
  }
  return kept;
}
